import { DonutChartProps } from "src/components/pages/audit/donutChart";
import { AuditDataView, AuditField, AuditResult, AuditStatus } from "src/store/auditSlice";
import { compareStringsIgnoreCase } from "src/utils/stringUtil";
import { INADEQUATE_INFORMATION, NO_OBJECT_INVOLVED } from "src/constants";
import { Model } from "src/store/modelManagementSlice";

export const highlightText = (clarifications: string[]): void => {
  const paragraph = document.getElementById("incident-description");
  if (paragraph) {
    let innerHTML = paragraph.innerHTML;

    clarifications.forEach((clarification) => {
      // Remove leading and trailing non-alphanumeric characters
      clarification = clarification.trim().replace(/^[^a-zA-Z0-9]+|[^a-zA-Z0-9]+$/g, "");
      const regex = new RegExp(
        `(\\([^)]*?${clarification.replace(/([()\\])/g, "\\$1")}[^)]*?\\))|(${clarification.replace(/([()\\])/g, "\\$1")})`,
        "gi",
      );

      innerHTML = innerHTML.replace(regex, (match, p1, p2) => {
        if (p1) {
          return `<span class="highlight">${p1}</span>`;
        } else if (p2) {
          return `<span class="highlight">${p2}</span>`;
        }
        return match;
      });
    });

    paragraph.innerHTML = innerHTML;

    const firstHighlight = paragraph.querySelector(".highlight");
    if (firstHighlight) {
      firstHighlight.scrollIntoView({
        behavior: "smooth",
      });
    }
  }
};

export const moveToTextTop = (): void => {
  const paragraph = document.getElementById("incident-description-container");
  if (paragraph) {
    paragraph.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

export const removeHighlightedText = (): void => {
  const paragraph = document.getElementById("incident-description");
  if (paragraph) {
    const innerHTML = paragraph.innerHTML;

    paragraph.innerHTML = innerHTML.replace(/<span class="highlight">|<\/span>/g, "");
  }
};

export const getRatio = (data: DonutChartProps[] | undefined): string => {
  if (!data || !Array.isArray(data)) {
    return "0%";
  }

  const unreviewedData = data.find((obj) => obj.label === "Unreviewed")?.value ?? 0;
  const reviewedData = data.find((obj) => obj.label === "Reviewed")?.value ?? 0;

  if (unreviewedData + reviewedData === 0) {
    return "0%";
  }

  let percentage = (unreviewedData / (reviewedData + unreviewedData)) * 100;
  return percentage.toFixed(0) + "%";
};

export const findNextUnreviewedCase = (list: AuditDataView[], incidentId?: string) => {
  const currentIndex = incidentId ? list.findIndex((item) => item.incidentId === incidentId) : -1;

  return (
    // Try to find unreviewed case after current index
    (currentIndex !== -1 &&
      list.slice(currentIndex + 1).find((item) => item.status === AuditStatus.UNREVIEWED)) ||
    // If not found, find any unreviewed case excluding current incidentId
    list
      .filter((item) => item.incidentId !== incidentId)
      .find((item) => item.status === AuditStatus.UNREVIEWED)
  );
};

export const isMultipleAnswerEqual = (answer1: string, answer2: string): boolean => {
  const answer1Set = answer1.toLowerCase().split("|").sort();
  const answer2Set = answer2.toLowerCase().split("|").sort();

  return (
    answer1Set.length === answer2Set.length &&
    answer1Set.every((item, index) => item === answer2Set[index])
  );
};

export const isAustinAnswerNoObjectInvolved = (field: AuditField) => {
  return isAustinAnswerNotExist(field) && field.fieldName.toLowerCase().includes("object");
};

export const isAustinAnswerNotExist = (field: AuditField) => {
  return field.austinAnswer === "" || compareStringsIgnoreCase(field.austinAnswer, "none");
};

export const isAnswerMatch = (austinAnswer: string, aiAnswer: string, many: boolean) => {
  // # hard code should be removed after refactor backend matching rule
  if (austinAnswer === "" && aiAnswer.toLowerCase() === NO_OBJECT_INVOLVED.toLowerCase()) {
    return true;
  }
  if (isAnswerInadequate(austinAnswer) || isAnswerInadequate(aiAnswer)) {
    return false;
  }
  return (
    compareStringsIgnoreCase(austinAnswer, aiAnswer) ||
    (many && isMultipleAnswerEqual(austinAnswer, aiAnswer))
  );
};

export const isAnswerInadequate = (answer: string) => {
  return (
    !answer ||
    compareStringsIgnoreCase(answer, "none") ||
    compareStringsIgnoreCase(answer, INADEQUATE_INFORMATION)
  );
};

export const isConfirmedAnswerValid = (field: AuditField) => {
  if (typeof field.confirmedAnswer === "string") {
    return (
      isAustinAnswerNoObjectInvolved(field) ||
      (field.confirmedAnswer &&
        !compareStringsIgnoreCase(field.confirmedAnswer, AuditResult.NEITHER))
    );
  } else {
    return field.confirmedAnswer && field.confirmedAnswer.length > 0;
  }
};

export const isFieldMultiSelected = (auditField: AuditField, model: Model) => {
  return (
    Object.values(model.fields).find((field) => field.name === auditField.fieldName)?.many || false
  );
};
